import React, { useMemo, useState } from "react";
import { Navigate, useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";
import { useMutation, useQuery, useQueryClient } from "react-query";
import DashboardWrapper from "../../components/Layout/DashboardWrapper";
import QueueHeader from "../../components/Queue/QueueHeader";
import { AppointmentPill, PatientAvatar, QueueEmptyState, WaitTimeBadge } from "../../components/Queue/QueueBits";
import VisitReasonBadge from "../../components/VisitReason/VisitReasonBadge";
import { getUserData, setStoragePatient } from "../../utils/storageUtils";
import { isDoctor } from "../../constants/roles";
import { setCurrentPatient } from "../../redux/users/actions";
import { getUserById } from "../../services/users";
import { getDoctorQueue, takeCharge } from "../../services/queue";
import { hm, QueueEntryApi } from "../../services/queue/types";
import { computeQueueStats, patientDisplayName } from "../../services/queue/stats";
import useQueueStream from "../../hooks/useQueueStream";

/**
 * Vue médecin (DEC-8) : ses patients présents en FIFO. Le premier est mis en
 * avant ; « Prendre en charge » ouvre la consultation et retire le patient.
 */
const QueueMedecin: React.FC = () => {
  const userData = getUserData();
  const allowed = isDoctor(userData);

  const dispatch = useDispatch();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [entries, setEntries] = useState<QueueEntryApi[]>([]);

  const doctorQuery = useQuery(["queue-doctor"], () => getDoctorQueue(), {
    enabled: allowed,
    refetchInterval: 60000, // filet de sécurité si le flux SSE tombe
    onSuccess: (result: any) => {
      if (result?.ok) setEntries((result.data ?? []) as QueueEntryApi[]);
    },
  });

  // Temps réel : le serveur pousse la file FIFO à jour à chaque changement.
  useQueueStream((queue) => setEntries(queue), allowed);

  const takeChargeMut = useMutation((entry: QueueEntryApi) => takeCharge(entry.id), {
    onSuccess: async (result: any, entry: QueueEntryApi) => {
      if (!result?.ok) return;
      const patientId = result.data?.patientId ?? entry.patientId;
      // Charge la fiche patient COMPLÈTE (âge, antécédents, titre…) avant de
      // naviguer : l'entrée de file ne porte que id + nom/prénom, ce qui
      // laissait la page consultation incomplète jusqu'au rafraîchissement.
      const full: any = await getUserById(patientId);
      const patient =
        full?.ok && full.data
          ? full.data
          : { id: patientId, firstName: entry.patientFirstName, lastName: entry.patientLastName };
      dispatch(setCurrentPatient(patient));
      setStoragePatient(patient);
      queryClient.invalidateQueries(["queue-doctor"]);
      navigate("/consultation");
    },
  });

  const stats = useMemo(() => computeQueueStats(entries), [entries]);
  const [nextEntry, ...followingEntries] = entries;
  const doctorLastName = ((userData as any)?.lastName ?? (userData as any)?.user?.lastName ?? "").toUpperCase();

  if (!allowed) {
    return <Navigate to="/" replace />;
  }

  return (
    <DashboardWrapper showSpinner={doctorQuery.isLoading && entries.length === 0}>
      <div className="max-w-[1080px] space-y-8">
        <div className="animate-in fade-in slide-in-from-bottom-2 duration-500 fill-mode-both">
          <QueueHeader
            eyebrow={doctorLastName ? `Consultation · Dr ${doctorLastName}` : "Consultation"}
            title="Ma liste d'attente"
            subtitle={
              <>
                <span className="font-semibold text-slate-700">
                  {stats.waitingCount} patient{stats.waitingCount > 1 ? "s" : ""}
                </span>{" "}
                en attente
                {stats.averageWaitMinutes != null && ` · attente moyenne ${stats.averageWaitMinutes} min`}
              </>
            }
          />
        </div>

        {nextEntry == null ? (
          <QueueEmptyState
            title="Aucun patient en attente"
            description="Les patients que la secrétaire vous assigne apparaîtront ici automatiquement, dans l'ordre d'arrivée."
          />
        ) : (
          <>
            <section
              aria-labelledby="next-patient-title"
              className="flex flex-col gap-6 rounded-[28px] border border-slate-200 bg-white p-6 shadow-sm sm:flex-row sm:items-center sm:justify-between sm:p-8 animate-in fade-in slide-in-from-bottom-2 duration-500 delay-100 fill-mode-both"
            >
              <div className="flex min-w-0 items-center gap-5">
                <PatientAvatar entry={nextEntry} size="lg" />
                <div className="min-w-0">
                  <p id="next-patient-title" className="text-[11px] uppercase tracking-[0.32em] text-emerald-700 font-semibold">
                    Patient suivant
                  </p>
                  <p className="mt-1.5 truncate text-2xl font-bold text-slate-900">{patientDisplayName(nextEntry)}</p>
                  <div className="mt-2.5 flex flex-wrap items-center gap-2 text-sm">
                    <VisitReasonBadge
                      label={nextEntry.visitReasonLabel}
                      color={nextEntry.visitReasonColor}
                      note={nextEntry.reasonNote}
                    />
                    <AppointmentPill entry={nextEntry} />
                    {nextEntry.arrivalTime && <span className="text-slate-500">Arrivée {hm(nextEntry.arrivalTime)}</span>}
                    <WaitTimeBadge minutes={nextEntry.waitingMinutes} suffix=" d'attente" />
                  </div>
                </div>
              </div>
              <button
                type="button"
                disabled={takeChargeMut.isLoading}
                onClick={() => takeChargeMut.mutate(nextEntry)}
                className="inline-flex shrink-0 items-center justify-center gap-2 rounded-xl bg-green-600 px-6 py-3.5 text-[15px] font-semibold text-white shadow-lg shadow-green-600/25 transition-all duration-300 hover:bg-green-700 active:bg-green-800 focus:outline-none focus:ring-4 focus:ring-emerald-500/30 disabled:opacity-60"
              >
                Prendre en charge <i className="bx bx-right-arrow-alt text-xl" aria-hidden />
              </button>
            </section>

            {followingEntries.length > 0 && (
              <section
                aria-labelledby="following-title"
                className="space-y-3 animate-in fade-in slide-in-from-bottom-2 duration-500 delay-200 fill-mode-both"
              >
                <div className="flex items-center justify-between">
                  <h2 id="following-title" className="text-[11px] font-semibold uppercase tracking-wider text-slate-500">
                    Ensuite · ordre d'arrivée
                  </h2>
                  <span className="text-xs text-slate-500">
                    {followingEntries.length} patient{followingEntries.length > 1 ? "s" : ""}
                  </span>
                </div>
                <ul className="divide-y divide-slate-100 rounded-2xl border border-slate-200 bg-white shadow-sm">
                  {followingEntries.map((entry, index) => (
                    <li
                      key={entry.id}
                      className="flex flex-wrap items-center justify-between gap-4 px-5 py-4 transition-colors hover:bg-emerald-50/40"
                    >
                      <div className="flex min-w-0 items-center gap-4">
                        <span className="w-6 text-center font-bold text-slate-400 tabular-nums">{index + 2}</span>
                        <PatientAvatar entry={entry} muted />
                        <div className="min-w-0">
                          <p className="truncate font-semibold text-slate-900">{patientDisplayName(entry)}</p>
                          <div className="mt-1 flex flex-wrap items-center gap-2 text-xs text-slate-500">
                            <VisitReasonBadge label={entry.visitReasonLabel} color={entry.visitReasonColor} note={entry.reasonNote} />
                            <AppointmentPill entry={entry} />
                          </div>
                        </div>
                      </div>
                      <div className="flex items-center gap-3">
                        <WaitTimeBadge minutes={entry.waitingMinutes} />
                        <button
                          type="button"
                          disabled={takeChargeMut.isLoading}
                          onClick={() => takeChargeMut.mutate(entry)}
                          className="h-10 rounded-xl border border-slate-200 bg-white px-4 text-[13px] font-medium text-slate-700 transition-colors hover:border-emerald-200 hover:bg-emerald-50 hover:text-emerald-800 disabled:opacity-60"
                        >
                          Prendre en charge
                        </button>
                      </div>
                    </li>
                  ))}
                </ul>
                <p className="text-xs text-slate-500">
                  « Prendre en charge » ouvre la consultation du patient et le retire de la liste.
                </p>
              </section>
            )}
          </>
        )}
      </div>
    </DashboardWrapper>
  );
};

export default QueueMedecin;
